import { type Edge, type Item } from './board'
import { type Effort, type TranscriptEntry } from './session'

// The example gallery: boards made with the real model through the eval
// harness (evals/gallery.ts), each with the conversation that made it. The
// files ship as static assets (public/gallery/<slug>.moot). To drop an
// example, delete its line here and its file.

export type GalleryEntry = {
  slug: string
  category: 'learning' | 'life'
  /** The language the conversation is in. */
  language: 'zh-Hant' | 'en'
  /** Title and blurb are in the example's own language, whatever the UI's:
   * the example is a real conversation, and a translated title would promise
   * something the conversation is not. */
  title: string
  blurb: string
  /**
   * The same example played in the other language (its slug): a viewer sees
   * the one in their language and not both.
   */
  counterpart?: string
  /** Health topics: shown with a note that it is AI-written, not medical advice. */
  medical?: boolean
  /**
   * Not listed: kept reachable by its link (/gallery/<slug>) but not shown to
   * people browsing, e.g. an early example that the board adds little to.
   */
  hidden?: boolean
  /** The model and effort that made it (shown quietly: several are not the product default). */
  made: { model: string; effort: Effort }
}

const DEFAULT = { model: 'claude-sonnet-5-5', effort: 'medium' } as const

export const GALLERY: GalleryEntry[] = [
  {
    slug: 'chest-pain',
    counterpart: 'chest-pain-en',
    category: 'learning',
    language: 'zh-Hant',
    title: '急診胸痛：檢查一項項回來，診斷跟著翻盤',
    blurb:
      'ECG 像 inferior STEMI，學長要給 heparin；你在剝離卡上補了雙臂血壓差，AI 讀到後要你先停。剝離和 STEMI 之間那條線，隨著結果從「別漏了」改成「病因」，晨會用的時間軸接在最後。',
    medical: true,
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
  {
    slug: 'move-tainan',
    counterpart: 'move-tainan-en',
    category: 'life',
    language: 'zh-Hant',
    title: '搬回台南還是留台北：兩段對話，同一塊白板',
    blurb:
      '你在爸媽卡上寫下真正的原因；另開一段對話唱反調，七張反駁各自連到它挑戰的卡。原本的對話回來逐張回應：哪張說對了就改，哪張不同意就說為什麼，最後寫成要跟阿哲談的卡。',
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
  {
    slug: 'thermo',
    counterpart: 'thermo-en',
    category: 'learning',
    language: 'zh-Hant',
    title: '熱、溫度、內能、熵、自由能的關係圖',
    blurb:
      '先畫出有標籤連線的關係圖；你自己畫的「溫度 → 內能：成正比」被指出只對理想氣體成立。冰融化的作業題剛好錯在這裡，每題做成例題卡接回它踩到的概念，兩題對照連在一起，最後一張錯誤整理。',
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
  {
    slug: 'chest-pain-en',
    counterpart: 'chest-pain',
    category: 'learning',
    language: 'en',
    title: 'Chest pain in the ED: results come in one by one, and the diagnosis turns over',
    blurb:
      'The ECG looks like an inferior STEMI and your senior wants heparin; you add the two-arm blood pressures to the dissection card, and the AI, reading them, tells you to hold. The line between dissection and STEMI goes from "don\'t miss" to "cause" as results arrive, and a timeline for morning report closes it.',
    medical: true,
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
  {
    slug: 'move-tainan-en',
    counterpart: 'move-tainan',
    category: 'life',
    language: 'en',
    title: 'Move back to Tainan or stay in Taipei: two conversations, one board',
    blurb:
      "You write the real reason on your parents' card; a second conversation argues for staying, each of its eight counter-arguments connected to the card it challenges. Back in the first, a new remote-work option reshapes the board, and it ends on a card to take into the talk with Jay.",
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
  {
    slug: 'thermo-en',
    counterpart: 'thermo',
    category: 'learning',
    language: 'en',
    title: 'How heat, temperature, internal energy, entropy and free energy relate',
    blurb:
      'A concept map with labelled connections first; the "proportional" arrow you drew from temperature to internal energy turns out to hold only for an ideal gas. The ice-melting homework trips on exactly that, and the night ends with a card of your mistakes, each pointing at the card it is about.',
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
  {
    // The board in moot's share image (scripts/share-assets.ts).
    slug: 'og-offer',
    hidden: true,
    category: 'life',
    language: 'zh-Hant',
    title: '要不要接新創的 offer',
    blurb:
      '月薪 9 萬變 11.5 萬，但要每天進辦公室：先把考量和它們的關係攤開，說出最在意的是學習之後，白板圍著它重排。',
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
  {
    slug: 'bayes',
    hidden: true,
    category: 'learning',
    language: 'zh-Hant',
    title: '直覺理解貝氏定理',
    blurb: '從一千個人去檢測開始，拆成九張短卡；你在卡上補了自己的快篩例子，它接著用上。',
    made: DEFAULT,
  },
  {
    slug: 'jlpt',
    hidden: true,
    category: 'learning',
    language: 'zh-Hant',
    title: '三個月準備 JLPT N2',
    blurb: '平日一小時、週末三小時，排出逐週任務與檢查點；被追問時逐列核對時間。',
    made: DEFAULT,
  },
  {
    slug: 'attention',
    hidden: true,
    category: 'learning',
    language: 'en',
    title: 'Explaining transformer attention to a team',
    blurb:
      'From zero, but fast: seven connected cards, then a question handed over from the board.',
    made: DEFAULT,
  },
  {
    slug: 'pneumonia',
    hidden: true,
    category: 'learning',
    language: 'zh-Hant',
    title: '社區型肺炎的初始用藥與降階',
    blurb: '72 歲、Ccr 40，依 ATS/IDSA 2019 開立初始用藥，再談 IV 轉口服與療程；被質疑時修正。',
    medical: true,
    made: DEFAULT,
  },
  {
    slug: 'kyoto',
    hidden: true,
    category: 'life',
    language: 'en',
    title: 'Five days in Kyoto with a 70-year-old mom',
    blurb:
      'At most 6 km a day, away from the crowds: a card per day, walking and travel times checked day by day.',
    made: DEFAULT,
  },
  {
    slug: 'car',
    hidden: true,
    category: 'life',
    language: 'zh-Hant',
    title: '要不要買一台 70 萬的車',
    blurb: '月薪 6.5 萬、存款 80 萬、跨區通勤：算總成本、折舊與機會成本，被追問時改正算式。',
    made: DEFAULT,
  },
  {
    slug: 'running',
    hidden: true,
    category: 'life',
    language: 'zh-Hant',
    title: '膝蓋受過傷，半年跑到 10K',
    blurb: '一週三天、從幾乎不運動開始：分階段的跑走計畫，說明比例怎麼來、何時要更保守。',
    made: DEFAULT,
  },
  {
    slug: 'bayes-high',
    hidden: true,
    category: 'learning',
    language: 'zh-Hant',
    title: '直覺理解貝氏定理（連成一條路）',
    blurb: '同一個問題，思考強度調高：十二張卡用箭頭從 1 串到 12，照順序讀。',
    made: { model: 'claude-sonnet-5-5', effort: 'high' },
  },
  {
    slug: 'bayes-opus',
    hidden: true,
    category: 'learning',
    language: 'zh-Hant',
    title: '直覺理解貝氏定理（卡片嵌在回覆裡）',
    blurb: '同一個問題換成 Opus：回覆裡直接嵌入要看的卡片，十一張卡用連線串起來。',
    made: { model: 'claude-opus-5-5', effort: 'medium' },
  },
]

/** "Sonnet 5.5 · medium": the model and effort, for people. */
export function madeWith(made: GalleryEntry['made']): string {
  const name = made.model
    .replace(/^claude-/, '')
    .replace(/-(\d+)-(\d+)$/, ' $1.$2')
    .replace(/^./, (c) => c.toUpperCase())
  return `${name} · ${made.effort}`
}

/**
 * The listed examples, in the viewer's language first, then the rest, each
 * group in list order; of an example played in both languages, only the
 * viewer's.
 */
export const galleryFor = (locale: string) => {
  const mine = (g: GalleryEntry) => g.language.startsWith('zh') === locale.startsWith('zh')
  const listed = GALLERY.filter((g) => !g.hidden)
  const shown = listed.filter(
    (g) => mine(g) || !listed.some((o) => o.slug === g.counterpart && mine(o)),
  )
  return [...shown.filter(mine), ...shown.filter((g) => !mine(g))]
}

export const gallerySlug = (slug: string) => GALLERY.find((g) => g.slug === slug) ?? null

/** An example's conversation, as the preview shows it. */
export type GalleryConversation = {
  id: string
  title: string
  model: string
  effort: Effort
  createdAt: number
  updatedAt: number
  entries: TranscriptEntry[]
}

/** What the preview of an example needs: the board and its conversations, read only. */
export type GalleryPreviewData = {
  items: Item[]
  edges: Edge[]
  /** Card text as a base64 Yjs update (or null: then `cards` markdown). */
  ydoc: string | null
  cards: Record<string, string>
  conversations: GalleryConversation[]
}
